import React from 'react';
import { Check } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';
import {
  RECRUIT_PROFILE_CAP,
  RECRUIT_PROFILE_OPTIONS,
  toggleRecruitProfile,
  useRecruitProfile,
  writeRecruitProfile
} from '../recruitProfile';
import type { RecruitProfileKind } from '../recruitProfile';
import { useCanEditClientNotes } from '../clientNotesStore';

/**
 * One chip group for 痛點 / 顧慮 / 優勢 (../recruitProfile): tap to pick, tap
 * again to drop, at most three. The same component answers in both places the
 * question is asked — the 快速資料 sheet and 招募 IDEA's own cards — and both
 * follow each other through useRecruitProfile.
 */

/** Each group's chip label. 痛點 and 顧慮 have their own ids; 優勢 has its own list. */
export const recruitProfileLabelId = (kind: RecruitProfileKind, key: string) =>
  kind === 'painPoints'
    ? `clientv2-recruit-pain-${key}`
    : kind === 'concerns'
    ? `clientv2-recruit-concern-${key}`
    : `clientv2-recruit-strength-${key}`;

export type RecruitProfileChipsProps = {
  clientId: string;
  kind: RecruitProfileKind;
  intl: IntlLike;
  /** Shows the group's name above the chips (the 快速資料 sheet); IDEA's card already has a title. */
  showTitle?: boolean;
};

const RecruitProfileChips = (props: RecruitProfileChipsProps) => {
  const { clientId, kind, intl, showTitle } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const profile = useRecruitProfile(clientId);
  const picked = profile[kind];
  const full = picked.length >= RECRUIT_PROFILE_CAP;
  // No factsheet, nowhere to save a pick: the chips show what is on file, and
  // say why none of them can be tapped.
  const canSave = useCanEditClientNotes(clientId);

  return (
    <div className="clientv2-recruit-chips" data-testid="recruit-profile-chips" data-kind={kind}>
      <div className="clientv2-recruit-chips-head">
        {showTitle && <span className="clientv2-recruit-chips-title">{t(`clientv2-recruit-profile-${kind}`)}</span>}
        <span className="clientv2-recruit-chips-count">
          {t('clientv2-recruit-profile-count', { count: picked.length, cap: RECRUIT_PROFILE_CAP })}
        </span>
      </div>
      {!canSave && (
        <p className="clientv2-recruit-readonly-note" data-testid="recruit-profile-readonly">
          {t('clientv2-notes-no-factsheet')}
        </p>
      )}
      <div className="clientv2-recruit-chips-list" role="group" aria-label={t(`clientv2-recruit-profile-${kind}`)}>
        {RECRUIT_PROFILE_OPTIONS[kind].map(key => {
          const selected = picked.includes(key);
          return (
            <button
              key={key}
              type="button"
              className={`clientv2-recruit-chip${selected ? ' is-selected' : ''}`}
              data-testid="recruit-profile-chip"
              data-key={key}
              aria-pressed={selected}
              // Past the cap, the rest wait until one is dropped.
              disabled={!canSave || (!selected && full)}
              onClick={() => writeRecruitProfile(clientId, toggleRecruitProfile(profile, kind, key))}
            >
              {selected && <Check size={12} aria-hidden="true" />}
              {t(recruitProfileLabelId(kind, key))}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RecruitProfileChips;
